import { useState, useEffect, useRef } from 'react';

const WHATSAPP_LINK = 'https://wa.me/573057096663?text=Hola%2C%20%C2%BFEn%20qu%C3%A9%20podemos%20ayudarte%3F';

const brandLinks = [
  { label: 'Chevrolet', href: '#chevrolet', color: 'text-blue-400' },
  { label: 'Suzuki',    href: '#suzuki',    color: 'text-red-400' },
  { label: 'Jeep',      href: '#jeep',      color: 'text-green-400' },
  { label: 'RAM',       href: '#ram',       color: 'text-slate-300' },
  { label: 'Dodge',     href: '#dodge',     color: 'text-rose-400' },
  { label: 'Isuzu',     href: '#isuzu',     color: 'text-amber-400' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [brandsOpen, setBrandsOpen] = useState(false);
  const [mobileBrandsOpen, setMobileBrandsOpen] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setBrandsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { label: 'Inicio',    href: '#hero' },
    { label: 'Productos', href: '#productos' },
    { label: 'Envíos',   href: '#envios' },
    { label: 'Nosotros', href: '#nosotros' },
    { label: 'Contacto', href: '#contacto' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-[#0d0d0d]/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 flex items-center justify-between h-[70px]">
        {/* Logo */}
        <a href="#hero" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/1c644e56cddf516eacde8781ded33937/e9560f414d66cc985767c3e7d3e0dff0.jpeg"
            alt="Chevrokee Automotriz Logo"
            className="h-10 w-10 rounded-full object-cover"
          />
          <span className="text-white font-bold text-lg tracking-wide whitespace-nowrap hidden sm:block">
            Chevrokee <span className="text-[#2563eb]">Automotriz</span>
          </span>
        </a>

        {/* Desktop Nav */}
        <ul className="hidden md:flex items-center gap-6">
          {/* Inicio */}
          <li>
            <a
              href="#hero"
              className="text-gray-300 hover:text-[#2563eb] text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Inicio
            </a>
          </li>

          {/* Marcas dropdown */}
          <li ref={dropdownRef} className="relative">
            <button
              onClick={() => setBrandsOpen(!brandsOpen)}
              className="flex items-center gap-1 text-gray-300 hover:text-[#2563eb] text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              Marcas
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${brandsOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {brandsOpen && (
              <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-44 bg-[#161616] border border-gray-700/60 rounded-xl overflow-hidden shadow-xl">
                {brandLinks.map((b) => (
                  <a
                    key={b.href}
                    href={b.href}
                    onClick={() => setBrandsOpen(false)}
                    className={`flex items-center gap-2.5 px-4 py-2.5 text-sm font-medium hover:bg-white/5 transition-colors cursor-pointer ${b.color}`}
                  >
                    <i className="ri-car-line text-xs opacity-70"></i>
                    {b.label}
                  </a>
                ))}
              </div>
            )}
          </li>

          {/* Resto de links */}
          {navLinks.slice(1).map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className="text-gray-300 hover:text-[#2563eb] text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
              >
                {link.label}
              </a>
            </li>
          ))}
        </ul>

        {/* CTA Button */}
        <a
          href={WHATSAPP_LINK}
          target="_blank"
          rel="nofollow noreferrer"
          className="hidden md:flex items-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer"
        >
          <i className="ri-whatsapp-line text-base"></i>
          Cotizar por WhatsApp
        </a>

        {/* Hamburger */}
        <button
          className="md:hidden text-white text-2xl cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Abrir menú"
        >
          <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-[#0d0d0d]/98 backdrop-blur-md border-t border-gray-800 px-4 py-6 flex flex-col gap-1">
          <a
            href="#hero"
            onClick={() => setMenuOpen(false)}
            className="text-gray-300 hover:text-[#2563eb] text-base font-medium transition-colors cursor-pointer px-2 py-2.5"
          >
            Inicio
          </a>

          {/* Marcas accordion mobile */}
          <div>
            <button
              onClick={() => setMobileBrandsOpen(!mobileBrandsOpen)}
              className="w-full flex items-center justify-between text-gray-300 hover:text-[#2563eb] text-base font-medium transition-colors cursor-pointer px-2 py-2.5"
            >
              Marcas
              <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${mobileBrandsOpen ? 'rotate-180' : ''}`}></i>
            </button>
            {mobileBrandsOpen && (
              <div className="ml-4 flex flex-col gap-1 mb-1 border-l border-gray-700/50 pl-4">
                {brandLinks.map((b) => (
                  <a
                    key={b.href}
                    href={b.href}
                    onClick={() => { setMenuOpen(false); setMobileBrandsOpen(false); }}
                    className={`flex items-center gap-2 text-sm font-medium py-2 transition-colors cursor-pointer ${b.color}`}
                  >
                    <i className="ri-car-line text-xs opacity-70"></i>
                    {b.label}
                  </a>
                ))}
              </div>
            )}
          </div>

          {navLinks.slice(1).map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={() => setMenuOpen(false)}
              className="text-gray-300 hover:text-[#2563eb] text-base font-medium transition-colors cursor-pointer px-2 py-2.5"
            >
              {link.label}
            </a>
          ))}

          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="nofollow noreferrer"
            className="flex items-center justify-center gap-2 bg-[#2563eb] hover:bg-[#1d4ed8] text-white text-sm font-semibold px-5 py-3 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer mt-3"
          >
            <i className="ri-whatsapp-line text-base"></i>
            Cotizar por WhatsApp
          </a>
        </div>
      )}
    </nav>
  );
}
